import {
  EvaluatorLink,
  getEvaluatorLinks,
  PaginatedResponse,
  QueryOptionsConfig,
  usePaginatedApi,
} from 'api';
import { EvaluatorLinksSortCategory } from 'types/types';
import { useSearchContext } from 'components/SearchFilter/SearchProvider';
import { QueryStatus } from '@tanstack/react-query';
import {
  getMinimumSearchCharacters,
  QueryParamsBase,
  SearchMode,
} from 'api/search';
import { ApiPaginationProps } from 'components/Pagination/Pagination';
import usePaginationV2, {
  DEFAULT_CARD_ITEMS_PER_PAGE,
  Paginator,
} from 'components/hooks/usePaginationV2';
import { QUERY_KEY_ROOTS } from 'variables';
import { useEffect, useRef } from 'react';
import { EvaluatorLinkFilter } from './EvaluatorLinkList/EvaluatorLinkList';

export interface PaginatedOrgEvaluatorLinkParams {
  organisationId?: string;
  createdBy?: string;
  searchTerm?: string;
  searchMode?: SearchMode;
  sort?: string;
  query?: string;
  responseSize?: number;
}

const useSortedEvaluatorLinks = (
  organisationId?: string | null,
  createdBy?: string | null,
  searchTerm?: string,
  searchMode: SearchMode = 'STARTS_WITH',
  initialLinkSortBy: EvaluatorLinksSortCategory = 'DATE_CREATED',
  config: QueryOptionsConfig<{
    items: EvaluatorLink[];
    totalCount?: number;
  }> = {},
): {
  paginator: Paginator<EvaluatorLink>;
  evaluatorStatus: QueryStatus;
  setLinkSortBy: (sortCategoryBy: EvaluatorLinksSortCategory) => void;
  sortCategory: EvaluatorLinksSortCategory;
  apiPaginationProps: ApiPaginationProps;
  showResultsLoadingState: boolean;
} => {
  const {
    searchState: { sortBy = initialLinkSortBy },
    setSearchState,
  } = useSearchContext();

  const setLinkSortBy = (sortCategoryBy: EvaluatorLinksSortCategory) => {
    setSearchState({ sortBy: sortCategoryBy });
  };

  const { enabled: userEnabled } = config ?? {};
  const enabledConfig = typeof userEnabled === 'boolean' ? userEnabled : true;

  const minChars = getMinimumSearchCharacters(searchMode);
  const hasValidSearchTerm = !!searchTerm && searchTerm.length >= minChars;

  const filterKey: EvaluatorLinkFilter = createdBy ? 'PRACTITIONER' : 'ALL';
  const hasLoadedRealDataRef = useRef<Set<EvaluatorLinkFilter>>(new Set());

  const {
    data,
    status: evaluatorStatus,
    isFetching,
    hasNextPage: hasMoreToLoad,
    fetchNextPage: loadMore,
    isPlaceholderData,
  } = usePaginatedApi<
    PaginatedResponse<EvaluatorLink>,
    EvaluatorLink,
    PaginatedOrgEvaluatorLinkParams & QueryParamsBase,
    { id: string | number }
  >({
    fetchPage: getEvaluatorLinks,
    queryKey: [
      QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINKS,
      QUERY_KEY_ROOTS.PAGINATED_DATA,
    ],
    getItemsFromPage: (p) => p.response ?? [],
    initialPageParam: undefined,
    queryParams: {
      ...(organisationId ? { organisationId } : {}),
      ...(createdBy ? { createdBy } : {}),
      ...(hasValidSearchTerm ? { searchTerm, searchMode } : {}),
      ...(sortBy ? { sort: sortBy } : {}),
    },
    placeholderData: enabledConfig ? (prev) => prev : undefined,
    enabled: enabledConfig,
  });

  const apiPaginationProps: ApiPaginationProps = {
    isFetching,
    hasMoreToLoad,
    loadMore,
    loadingState: evaluatorStatus,
  };

  const paginator = usePaginationV2(
    data?.items ?? [],
    DEFAULT_CARD_ITEMS_PER_PAGE,
    data?.totalCount,
  );

  useEffect(() => {
    if (evaluatorStatus === 'success' && !isPlaceholderData) {
      hasLoadedRealDataRef.current.add(filterKey);
    }
  }, [filterKey, evaluatorStatus, isPlaceholderData]);

  const showResultsLoadingState =
    isFetching &&
    isPlaceholderData &&
    !hasLoadedRealDataRef.current.has(filterKey);

  return {
    paginator,
    evaluatorStatus,
    setLinkSortBy,
    sortCategory: sortBy as EvaluatorLinksSortCategory,
    apiPaginationProps,
    showResultsLoadingState,
  };
};

export default useSortedEvaluatorLinks;
